import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatDividerModule } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { BasePageComponent } from '../../../../core/classes/base-page';
import { HelpersService } from '../../../../core/services/helpers.service';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { LoadingService } from '../../../../core/services/loading.service';
import { ReportService } from '../../../../core/api/report.service';
import { AuthService } from '../../../../core/services/auth.service';
import { FileService } from '../../../../core/api/file.service';
import { debounceTime } from 'rxjs';
import { GenerateExcelComponent } from '../generate-excel/generate-excel.component';
import { FilePreviewComponent } from '../../../../components/file-preview/file-preview.component';

@Component({
  selector: 'app-reimbursement',
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatPaginatorModule,
    MatDividerModule,
    MatTabsModule,
    MatMenuModule,
    MatCheckboxModule,
    FormsModule,
    ReactiveFormsModule,
  ],
  templateUrl: './reimbursement.component.html',
  styleUrl: './reimbursement.component.scss',
})
export class ReimbursementComponent
  extends BasePageComponent<any>
  implements OnInit
{
  searchFormControl = new FormControl();
  counter = {
    draftCount: 0,
    sentCount: 0,
    approvedCount: 0,
    rejectedCount: 0,
    reimbursedCount: 0,
  };
  currentFilter: any;
  column: any[] = [];

  constructor(
    public override helpers: HelpersService,
    public override router: Router,
    public override activatedRoute: ActivatedRoute,
    public override dialog: MatDialog,
    public override loading: LoadingService,
    public override service: ReportService,
    public override authService: AuthService,
    public fileService: FileService
  ) {
    super(
      helpers,
      router,
      activatedRoute,
      dialog,
      loading,
      service,
      authService
    );
    this.column = [
      {
        value: 'Ref ID',
        key: 'refId',
        active: true,
      },
      {
        value: 'Title',
        key: 'title',
        active: true,
      },
      {
        value: 'Type Data',
        key: 'type_data',
        active: true,
      },
      {
        value: 'Expanse Category',
        key: 'expanseCategory',
        active: true,
      },
      {
        value: 'Status',
        key: 'status',
        active: true,
      },
      {
        value: 'Date',
        key: 'date',
        active: true,
      },
      {
        value: 'Amount',
        key: 'amount',
        active: true,
      },
    ];
    this.displayedColumns = this.column.map((col) => col.key);
    this.sortActive = 'date';
    this.sortDirection = 'desc';
    this.q.status = null;
  }

  checkColumn() {
    this.displayedColumns = this.column
      .filter((col) => col.active)
      .map((col) => col.key);
  }

  override ngOnInit(): void {
    super.ngOnInit();

    this.searchFormControl.valueChanges
      .pipe(debounceTime(300))
      .subscribe((val: any) => {
        this.q.search = val;
        this.getData();
      });
  }

  override afterInit(): void {
    if (!this.data) this.loading.startLoading();
    this.q.find = 'reimburse';
    this.service
      .getAllReport(this.q)
      .then((data) => {
        this.counter = data;
        this.assignData(data?.reports);
        this.afterGetData();
        this.loading.endLoading();
      })
      .catch((err) => {
        this.helpers.sbError(err);
        this.loading.endLoading();
      });
  }

  override getData(): void {
    this.loading.startLoading();
    this.q.find = 'reimburse';
    this.service
      .getAllReport(this.q)
      .then((data) => {
        this.counter = data;
        this.assignData(data?.reports);
        this.afterGetData();
        this.loading.endLoading();
      })
      .catch((err) => {
        this.helpers.sbError(err);
        this.loading.endLoading();
      });
  }

  generateExcel(): void {
    this.dialog
      .open(GenerateExcelComponent, {
        width: '90vw',
        maxHeight: '95vh',
        data: {
          find: this.q.find ?? 'reimburse',
          page: this.q.page,
          limit: this.q.limit,
          search: this.q.search ?? '',
          status: this.q.status ?? 'all',
        },
      })
      .afterClosed()
      .subscribe((item) => {
        if (item) {
        }
      });
  }

  download(item: any) {
    const targetFile = this.helpers.resolveTargetFile(item);
    if (targetFile?.id) {
      this.dialog
        .open(FilePreviewComponent, {
          width: '70vw',
          maxWidth: '70vw',
          maxHeight: '95vh',
          data: targetFile,
        })
        .afterClosed()
        .subscribe((item) => {
          if (item) {
          }
        });
    } else {
      this.helpers.sbError('Tidak ada file yang bisa diunduh');
    }
  }

  changeFilter(filter: any): void {
    if (this.currentFilter === filter || filter == null) {
      this.currentFilter = null;
    } else {
      this.currentFilter = filter;
    }
    this.q.page = 1;
    this.q.status = this.currentFilter;
    this.getData();
  }

  getStatusClass(status?: string | null): string {
    switch (status) {
      case 'Draft':
        return 'bg-slate-100 text-slate-900';
      case 'Sent':
        return 'bg-amber-100 text-amber-900';
      case 'Approved':
        return 'bg-blue-100 text-blue-900';
      case 'Rejected':
      case 'Rejected Finance':
      case 'Settlement Rejected':
        return 'bg-rose-100 text-rose-900';
      case 'Sent To Finance':
      case 'Settlement Sent':
        return 'bg-indigo-100 text-indigo-900';
      case 'Finance Approve':
        return 'bg-teal-100 text-teal-900';
      case 'Reimbursed':
      case 'Disbursed':
        return 'bg-emerald-100 text-emerald-900';
      case 'Settlement Verified':
        return 'bg-blue-100 text-blue-900';
      case 'Settlement Sent To Finance':
        return 'bg-violet-100 text-violet-900';
      case 'Closed':
        return 'bg-slate-100 text-slate-900';
      default:
        return 'bg-slate-100 text-slate-900';
    }
  }
}
